import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ToastService } from 'src/app/modules/shared/services/toast/toast.service';

/**
 * Modal de ajuste da foto (recorte + zoom).
 *
 * Apresenta uma máscara fixa central (quadrada) que define a área final da
 * imagem. A foto fica por trás da máscara, com o entorno desfocado (blur). O usuário
 * não move o frame: apenas reposiciona a imagem por meio de drag e controla o
 * enquadramento com o zoom (slider). A imagem sempre cobre completamente a área
 * da máscara (sem vazios). Ao confirmar, devolve via dismiss um `File` JPEG já
 * recortado na proporção 1:1 (campo `objetoOriginal` do contrato).
 *
 * Visão geral do funcionamento com <canvas>:
 * - Mantemos a foto em um `Image` em memória e a desenhamos a cada frame em um
 *   `<canvas>` através do seu contexto 2D (`ctx`), que é a "API de pincel".
 * - O que se vê é só um PREVIEW. O recorte final (confirm) é gerado à parte,
 *   recortando direto da imagem original em alta resolução.
 * - Coordenadas: trabalhamos em "pixels de canvas". A imagem é posicionada por
 *   uma escala (`scale`) e um deslocamento/pan (`offsetX/Y`).
 */
@Component({
  selector: 'app-foto-crop-modal',
  templateUrl: './foto-crop-modal.component.html',
  styleUrls: ['./foto-crop-modal.component.scss'],
})
export class FotoCropModalComponent implements AfterViewInit {
  /** Arquivo de imagem original escolhido pelo usuário. */
  @Input() file!: File;
  /** Nome final do arquivo recortado. */
  @Input() fileName: string = 'foto.jpg';

  // Referência ao elemento <canvas> do template (resolvida só após a view
  // inicializar — ver ngAfterViewInit).
  @ViewChild('canvas', { static: false })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  // Máscara fixa central (quadrada) que define o recorte final (1:1).
  readonly MASK_SIZE = 240;
  // Margem máxima de desfoque exibida ao redor da máscara (px por lado).
  readonly MAX_BLUR = 52;
  // Resolução da imagem recortada exportada (1:1).
  readonly OUTPUT_SIZE = 600;

  // Dimensões do canvas — dinâmicas: máscara + a margem de blur que houver de
  // imagem além da máscara (limitada a MAX_BLUR). O fundo não tem tamanho fixo.
  canvasWidth = this.MASK_SIZE;
  canvasHeight = this.MASK_SIZE;

  // Zoom controlado pelo slider. 1 = a imagem apenas cobre a máscara.
  zoom: number = 1;
  minZoom: number = 1;
  maxZoom: number = 5;

  // Imagem original (em memória) e flag de "já decodificada".
  private image = new Image();
  private imageLoaded = false;
  // Deslocamento (pan) da imagem dentro do canvas, em pixels de canvas.
  // (0,0) = imagem centralizada; valores deslocam a imagem sob a máscara.
  private offsetX = 0;
  private offsetY = 0;
  // Escala mínima para a imagem cobrir a máscara (definida em fitImage()).
  // A escala efetiva usada ao desenhar é baseScale * zoom (ver getter `scale`).
  private baseScale = 1;

  // Estado do arraste: âncora inicial para a imagem acompanhar o cursor.
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;

  isReady: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private toastService: ToastService,
  ) {}

  // O <canvas> (ViewChild) só existe depois que a view é inicializada.
  ngAfterViewInit(): void {
    this.loadImage();
  }

  // Canto superior esquerdo da máscara: ela é sempre centralizada no canvas,
  // então a sobra (canvas - máscara) é dividida igualmente entre os dois lados.
  private get maskLeft(): number {
    return (this.canvasWidth - this.MASK_SIZE) / 2;
  }

  private get maskTop(): number {
    return (this.canvasHeight - this.MASK_SIZE) / 2;
  }

  // Lê o File como data URL (base64) e aponta o `Image` em memória para ela.
  // Só quando a imagem termina de decodificar (image.onload) é que temos as
  // dimensões reais (image.width/height) para calcular a escala e desenhar.
  private loadImage(): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.image.onload = () => {
        this.imageLoaded = true;
        this.fitImage();
        this.draw();
        this.isReady = true;
      };
      this.image.src = reader.result as string;
    };
    reader.readAsDataURL(this.file);
  }

  /** Escala base mínima para a imagem cobrir toda a máscara (sem vazios). */
  private fitImage(): void {
    // Usamos o MAIOR entre as razões de largura e altura: assim nenhum lado da
    // imagem fica menor que a máscara (cobre por inteiro, "object-fit: cover").
    const scaleX = this.MASK_SIZE / this.image.width;
    const scaleY = this.MASK_SIZE / this.image.height;
    this.baseScale = Math.max(scaleX, scaleY);
    this.offsetX = 0;
    this.offsetY = 0;
    this.syncViewport();
  }

  // Escala efetiva aplicada à imagem: o "cover" mínimo da máscara vezes o zoom.
  private get scale(): number {
    return this.baseScale * this.zoom;
  }

  /**
   * Posição (canto superior esquerdo) e tamanho com que a imagem é desenhada.
   * `drawWidth`/`drawHeight` são as dimensões já escaladas; `drawX`/`drawY`
   * centralizam a imagem no canvas ((canvas - imagem) / 2) e então aplicam o
   * pan do usuário.
   */
  private get drawOrigin(): {
    drawX: number;
    drawY: number;
    drawWidth: number;
    drawHeight: number;
  } {
    const drawWidth = this.image.width * this.scale;
    const drawHeight = this.image.height * this.scale;
    const drawX = (this.canvasWidth - drawWidth) / 2 + this.offsetX;
    const drawY = (this.canvasHeight - drawHeight) / 2 + this.offsetY;
    return { drawX, drawY, drawWidth, drawHeight };
  }

  /**
   * Renderiza o preview em duas camadas da MESMA imagem (mesma posição/escala):
   *   1) a imagem inteira borrada — vira o "entorno" fora da máscara;
   *   2) a imagem nítida recortada à máscara — a área que será exportada.
   * Por fim, desenha os cantos (brackets) que marcam a máscara.
   */
  private draw(): void {
    if (!this.imageLoaded || !this.canvasRef) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    // Atribuir canvas.width/height redimensiona o bitmap E o limpa. Só fazemos
    // isso quando o tamanho realmente mudou (ao dar zoom), para não limpar à toa.
    if (canvas.width !== this.canvasWidth) {
      canvas.width = this.canvasWidth;
    }
    if (canvas.height !== this.canvasHeight) {
      canvas.height = this.canvasHeight;
    }
    // O contexto 2D é por onde todo desenho acontece.
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const { drawX, drawY, drawWidth, drawHeight } = this.drawOrigin;
    const maskLeft = this.maskLeft;
    const maskTop = this.maskTop;

    // Apaga o quadro anterior (draw() roda a cada interação).
    ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

    // Entorno desfocado: a própria imagem na posição/zoom do usuário, borrada.
    // É o que estiver fora da máscara central (não um cover redimensionado).
    // `ctx.filter` aplica o blur ao próximo drawImage; depois zeramos.
    ctx.filter = 'blur(6px)';
    ctx.drawImage(this.image, drawX, drawY, drawWidth, drawHeight);
    ctx.filter = 'none';

    // Área nítida dentro da máscara central.
    // save()/clip()/restore(): clip() restringe os desenhos seguintes ao
    // retângulo da máscara, então o drawImage nítido só "pinta" dentro dela;
    // restore() remove esse recorte para o que vem depois (os cantos).
    ctx.save();
    ctx.beginPath();
    ctx.rect(maskLeft, maskTop, this.MASK_SIZE, this.MASK_SIZE);
    ctx.clip();
    ctx.drawImage(this.image, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();

    // Cantos (brackets) que marcam a máscara.
    this.drawMaskCorners(ctx, maskLeft, maskTop, this.MASK_SIZE);
  }

  /**
   * Desenha os 4 "cantos" (brackets em formato de L) que marcam a máscara, em
   * vez de uma borda completa. O retângulo da máscara é dado por left/top/size.
   *
   * Como o canvas desenha por "caminhos" (paths):
   *   - moveTo(x, y) levanta a caneta e a reposiciona SEM traçar;
   *   - lineTo(x, y) traça uma reta do ponto atual até (x, y).
   * Cada canto é um "L" = dois lineTo a partir do vértice. Acumulamos os 4 Ls
   * em um único path (com moveTo separando cada um) e só no fim chamamos
   * stroke(), que traça tudo de uma vez — nada aparece antes do stroke().
   *
   * `legLength` é o comprimento de cada perna do L. lineJoin 'miter' deixa o
   * vértice do L "em bico" (canto vivo); lineCap 'butt' corta as pontas retas.
   */
  private drawMaskCorners(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    size: number,
  ): void {
    // Bordas direita e inferior da máscara (derivadas do canto + tamanho).
    const right = left + size;
    const bottom = top + size;
    const legLength = 26; // comprimento de cada perna do "L"

    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'miter';
    ctx.lineCap = 'butt';

    ctx.beginPath();
    // Superior esquerdo: perna vertical descendo até o vértice e virando p/ a direita.
    ctx.moveTo(left, top + legLength);
    ctx.lineTo(left, top);
    ctx.lineTo(left + legLength, top);
    // Superior direito
    ctx.moveTo(right - legLength, top);
    ctx.lineTo(right, top);
    ctx.lineTo(right, top + legLength);
    // Inferior direito
    ctx.moveTo(right, bottom - legLength);
    ctx.lineTo(right, bottom);
    ctx.lineTo(right - legLength, bottom);
    // Inferior esquerdo
    ctx.moveTo(left + legLength, bottom);
    ctx.lineTo(left, bottom);
    ctx.lineTo(left, bottom - legLength);
    // Traça de uma vez os 4 cantos acumulados no path.
    ctx.stroke();
  }

  /**
   * Recalcula o tamanho do canvas (máscara + margem de blur conforme o que há
   * de imagem além da máscara, limitada a MAX_BLUR) e clampa o arraste à
   * MÁSCARA: a imagem sempre cobre a máscara, mas pode ser arrastada até os
   * cantos. A margem de blur só é preenchida onde há imagem além da máscara.
   */
  private syncViewport(): void {
    // Tamanho da imagem já escalada.
    const scaledWidth = this.image.width * this.scale;
    const scaledHeight = this.image.height * this.scale;

    // Sobra da imagem além da máscara, por lado, limitada a MAX_BLUR.
    // Se a imagem não ultrapassa a máscara (ex.: 1:1 no zoom 1), a margem é 0.
    const marginX = Math.min(
      this.MAX_BLUR,
      Math.max(0, (scaledWidth - this.MASK_SIZE) / 2),
    );
    const marginY = Math.min(
      this.MAX_BLUR,
      Math.max(0, (scaledHeight - this.MASK_SIZE) / 2),
    );

    // Canvas = máscara + margem de blur dos dois lados.
    this.canvasWidth = Math.round(this.MASK_SIZE + 2 * marginX);
    this.canvasHeight = Math.round(this.MASK_SIZE + 2 * marginY);

    // Limite do pan relativo à MÁSCARA (não ao canvas): permite arrastar até a
    // borda da imagem coincidir com a borda da máscara — ou seja, dá para
    // enquadrar os cantos. Ao chegar na borda, a margem de blur daquele lado
    // deixa de ter imagem e mostra o fundo do stage (comportamento esperado).
    const maxOffsetX = Math.max(0, (scaledWidth - this.MASK_SIZE) / 2);
    const maxOffsetY = Math.max(0, (scaledHeight - this.MASK_SIZE) / 2);

    // Mantém o pan dentro de [-max, +max].
    this.offsetX = Math.min(maxOffsetX, Math.max(-maxOffsetX, this.offsetX));
    this.offsetY = Math.min(maxOffsetY, Math.max(-maxOffsetY, this.offsetY));
  }

  // Slider de zoom (ion-range emite o valor em event.detail.value).
  onZoomChange(event: any): void {
    const value = event?.detail?.value ?? event;
    this.zoom = Number(value);
    this.syncViewport();
    this.draw();
  }

  // ----- Arraste da imagem (mouse + touch) -----

  // Início do arraste: guarda (cursor - offset atual) como âncora, para que ao
  // mover o offset acompanhe o cursor mantendo o ponto "agarrado" sob ele.
  onPointerDown(event: MouseEvent | TouchEvent): void {
    if (!this.imageLoaded) {
      return;
    }
    this.isDragging = true;
    const point = this.getPoint(event);
    this.dragStartX = point.x - this.offsetX;
    this.dragStartY = point.y - this.offsetY;
  }

  // Durante o arraste: novo offset = posição do cursor - âncora inicial.
  // preventDefault evita scroll/seleção da página enquanto se arrasta.
  onPointerMove(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) {
      return;
    }
    event.preventDefault();
    const point = this.getPoint(event);
    this.offsetX = point.x - this.dragStartX;
    this.offsetY = point.y - this.dragStartY;
    this.syncViewport();
    this.draw();
  }

  onPointerUp(): void {
    this.isDragging = false;
  }

  // Normaliza coordenadas de mouse e touch para {x, y} (clientX/clientY = px
  // relativos à viewport). Como o offset usa deltas, a origem não importa.
  private getPoint(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if ('touches' in event) {
      const touch = event.touches[0];
      return { x: touch.clientX, y: touch.clientY };
    }
    return { x: event.clientX, y: event.clientY };
  }

  // Fecha sem alterações (descarta o ajuste).
  cancel(): void {
    this.modalCtrl.dismiss(null, 'cancel');
  }

  /**
   * Gera o recorte final. Em vez de copiar o canvas de preview (que tem blur e
   * cantos), recortamos direto da imagem ORIGINAL, em alta resolução.
   *
   * Convertendo da máscara (coords do canvas) de volta para coords da imagem
   * original: basta dividir pela escala. O que aparece dentro da máscara
   * corresponde ao quadrado (sourceX, sourceY) de lado sourceSize na imagem.
   * Os Math.max/min apenas protegem contra pequenos estouros de arredondamento.
   */
  confirm(): void {
    if (!this.imageLoaded) {
      return;
    }

    const { drawX, drawY } = this.drawOrigin;
    const scale = this.scale;

    // Região da máscara mapeada para as coordenadas da imagem original.
    // sourceSize = lado do quadrado de origem; sourceX/sourceY = seu canto
    // superior esquerdo.
    const sourceSize = this.MASK_SIZE / scale;
    const sourceX = Math.max(
      0,
      Math.min((this.maskLeft - drawX) / scale, this.image.width - sourceSize),
    );
    const sourceY = Math.max(
      0,
      Math.min((this.maskTop - drawY) / scale, this.image.height - sourceSize),
    );

    // Canvas de saída (fora da tela) no tamanho final 1:1.
    const output = document.createElement('canvas');
    output.width = this.OUTPUT_SIZE;
    output.height = this.OUTPUT_SIZE;
    const outputCtx = output.getContext('2d');
    if (!outputCtx) {
      this.toastService.showError('Não foi possível processar a imagem.');
      return;
    }

    // Fundo branco (JPEG não tem transparência).
    outputCtx.fillStyle = '#ffffff';
    outputCtx.fillRect(0, 0, this.OUTPUT_SIZE, this.OUTPUT_SIZE);
    // drawImage de 9 argumentos: (img, sx, sy, sW, sH, dx, dy, dW, dH).
    // Copia o quadrado de origem (sourceX, sourceY, sourceSize, sourceSize) da
    // imagem para todo o canvas de saída, redimensionando p/ OUTPUT_SIZE.
    outputCtx.drawImage(
      this.image,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      this.OUTPUT_SIZE,
      this.OUTPUT_SIZE,
    );

    // toBlob é assíncrono: gera o JPEG (qualidade 0.92) e, com o blob pronto,
    // devolve um `File` ao chamador via dismiss('confirm').
    output.toBlob(
      (blob) => {
        if (!blob) {
          this.toastService.showError('Não foi possível processar a imagem.');
          return;
        }
        const croppedFile = new File([blob], this.fileName, {
          type: 'image/jpeg',
        });
        this.modalCtrl.dismiss(croppedFile, 'confirm');
      },
      'image/jpeg',
      0.92,
    );
  }
}
